import React, { useState, useEffect, useRef } from "react";
import API from "../../api/axios";
import useAdminScope from "../../hooks/useAdminScope";
import ClassTimetable from "./ClassTimetable";
import SecondLanguageAssign from "./SecondLanguageAssign";
import {
  Calendar,
  Clock,
  User,
  Home,
  Plus,
  Trash2,
  Edit,
  X,
  Loader2,
  Save,
  AlertCircle,
  CheckCircle2,
  Filter,
  BookOpen,
  Layers,
} from "lucide-react";

const daysMapping = {
  sun: "الأحد",
  mon: "الاثنين",
  tue: "الثلاثاء",
  wed: "الأربعاء",
  thu: "الخميس",
};
const dayOrder = Object.keys(daysMapping);

// الحصص ومواعيدها جاية من صفحة "مواعيد الحصص" (مجموعة لكل مراحل وأيام معينة)،
// لأنها بتختلف حسب المرحلة وحسب اليوم. السيرفر هو اللي بياخد الميعاد منها وقت
// الحفظ — هنا بنعرضها بس عشان الأدمن يشوف الوقت وهو بيختار.
const PERIOD_NAMES = ["", "الأولى", "الثانية", "الثالثة", "الرابعة", "الخامسة", "السادسة", "السابعة", "الثامنة", "التاسعة", "العاشرة", "الحادية عشرة", "الثانية عشرة"];

// The teacher picker's value for a period that names only a subject (طابور،
// نشاط، مكتبة) — shown on the class's timetable, taught by nobody in the app.
// Posted to the server as an empty teacher.
const NO_TEACHER = "__none__";

// The subject picker's value for «مادة ثانية»: one period the class spends
// split between languages (French and German, say), each with its teacher —
// one timetable row per language on the server (Schedule.secondLanguage).
const SECOND_LANGUAGE = "__second__";
const emptyOptions = () => [
  { subjectId: "", teacherId: "" },
  { subjectId: "", teacherId: "" },
];

const SchedulesPage = () => {
  const { canEdit } = useAdminScope();
  const [schedules, setSchedules] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [bells, setBells] = useState([]);
  // من صفحة "إسناد الفصول": مين بيدرّس كل مادة في كل فصل.
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // حالات الفلترة (الصف والفصل) لعرض الكروت
  const [selectedClassroom, setSelectedClassroom] = useState("");
  // The page opens on the first class's timetable; after that the admin's
  // own choice (including "every class") is left alone across reloads.
  const pickedFirstClass = useRef(false);
  const [todayOnly, setTodayOnly] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // The rows a delete removes — a «مادة ثانية» period goes as a whole.
  const [deleteIds, setDeleteIds] = useState(null);
  // The class whose pupils are being given their «مادة ثانية» language.
  const [languageClass, setLanguageClass] = useState(null);

  // ---- منشئ جدول الفصل — الأساس هنا الفصل، مش المعلم ----
  // تختار المرحلة والفصل مرة واحدة، وبعدها تضيف كل حصص الفصل ده يوم بيوم
  // من غير ما تعيد اختيار الفصل تاني في كل مرة.
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderGrade, setBuilderGrade] = useState("");
  const [builderClassroom, setBuilderClassroom] = useState("");
  const [addingDay, setAddingDay] = useState(null);
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  // Whether the period being edited already has no teacher. Only then is
  const [periodForm, setPeriodForm] = useState({
    periods: [],
    subjectId: "",
    teacherId: "",
    options: emptyOptions(),
  });
  // The rows the period being edited takes the place of: the lesson itself,
  // or every language of a «مادة ثانية» period.
  const [replaceIds, setReplaceIds] = useState([]);
  const [periodSaving, setPeriodSaving] = useState(false);
  const [periodError, setPeriodError] = useState("");

  // ترتيب أيام الأسبوع زي JavaScript's Date.getDay() (0 = الأحد ... 6 = السبت)
  // الجمعة والسبت مش موجودين في daysMapping أصلًا (مفيش حصص فيهم)، فلو النهاردة
  // جمعة أو سبت هتفضل القيمة null ومفيش يوم هيتطابق معاها (يعني القائمة هتفضل فاضية،
  // وده صح لأنه مفيش حصص أصلًا في يوم إجازة).
  const jsDayToKey = ["sun", "mon", "tue", "wed", "thu", null, null];
  const todayKey = jsDayToKey[new Date().getDay()];

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [schRes, teachRes, classRes, subjRes, bellRes, assignRes] = await Promise.all([
        API.get("/schedules"),
        API.get("/teacher"),
        API.get("/classrooms"),
        API.get("/subjects"),
        API.get("/bell-schedules"),
        API.get("/class-assignments").catch(() => ({ data: { data: [] } })),
      ]);
      setAssignments(assignRes.data?.data || []);
      setSchedules(Array.isArray(schRes.data) ? schRes.data : []);
      setTeachers(teachRes.data.data || []);
      const loadedClassrooms = Array.isArray(classRes.data) ? classRes.data : [];
      setClassrooms(loadedClassrooms);
      if (!pickedFirstClass.current && loadedClassrooms.length) {
        pickedFirstClass.current = true;
        const first = [...loadedClassrooms].sort(
          (a, b) =>
            String(a.grade?.name || "").localeCompare(String(b.grade?.name || ""), "ar") ||
            String(a.name).localeCompare(String(b.name), "ar"),
        )[0];
        setSelectedClassroom(first._id);
      }
      setSubjects(Array.isArray(subjRes.data) ? subjRes.data : []);
      setBells(bellRes.data.data || []);
    } catch (err) {
      showToast("حدث خطأ أثناء تحميل البيانات", "error");
    } finally {
      setLoading(false);
    }
  };

  // The other languages of a «مادة ثانية» period, the lesson itself included.
  const siblingsOf = (lesson) =>
    lesson?.secondLanguage
      ? schedules.filter(
          (s) =>
            s.secondLanguage &&
            s.classroom?._id === lesson.classroom?._id &&
            s.day === lesson.day &&
            s.period === lesson.period,
        )
      : [lesson];

  const askDelete = (lesson) => setDeleteIds(siblingsOf(lesson).map((s) => s._id));

  const handleDelete = async () => {
    if (!deleteIds?.length) return;
    try {
      for (const id of deleteIds) {
        // eslint-disable-next-line no-await-in-loop -- one period's two or three rows.
        await API.delete(`/schedules/${id}`);
      }
      showToast("تم حذف الحصة بنجاح", "success");
      fetchInitialData();
      setDeleteIds(null);
    } catch (err) {
      showToast("فشل حذف الحصة", "error");
      setDeleteIds(null);
    }
  };

  // تصفية الجداول بناءً على الفصل المختار وحصص اليوم فقط
  const filteredSchedules = schedules
    .filter((s) => !selectedClassroom || s.classroom?._id === selectedClassroom)
    .filter((s) => !todayOnly || s.day === todayKey);

  // ---- منطق منشئ الجدول ----

  // المراحل مُستخرجة من الفصول نفسها (كل فصل معاه grade populated) بدل ما
  // نجيب endpoint تاني للمراحل.
  const grades = Array.from(
    new Map(
      classrooms
        .filter((c) => c.grade?._id)
        .map((c) => [c.grade._id, c.grade]),
    ).values(),
  );

  const gradeClassrooms = classrooms.filter(
    (c) => c.grade?._id === builderGrade,
  );

  const currentClassroom = classrooms.find((c) => c._id === builderClassroom);

  // المواد اللي بتتدرّس للمرحلة دي بس — سواء محدّدة بالاسم أو "كل المراحل".
  const gradeSubjects = subjects.filter(
    (s) =>
      s.allGrades ||
      (s.grades || []).some((g) => (g._id || g) === builderGrade),
  );

  // المعلم المسند للمادة دي في الفصل ده (لو اتحدد في "إسناد الفصول").
  const assignedTeacherFor = (subjectId) =>
    assignments.find(
      (a) => a.classroom?._id === builderClassroom && a.subject?._id === subjectId,
    )?.teacher;
  const assignedTeacher = assignedTeacherFor(periodForm.subjectId);

  // المعلمين اللي بيدرّسوا المادة المختارة ومسموحلهم بالمرحلة دي — ولو المادة
  // متسندة لمعلم في الفصل ده، يبقى هو بس.
  const eligibleTeachers = teachers.filter(
    (t) =>
      (assignedTeacher
        ? t._id === assignedTeacher._id
        : (t.subjects || []).some((s) => s._id === periodForm.subjectId) &&
          (t.teachingGrades || []).some((g) => g._id === builderGrade)),
  );

  const classroomSchedules = schedules.filter(
    (s) => s.classroom?._id === builderClassroom,
  );

  const periodsForDay = (day) =>
    classroomSchedules
      .filter((s) => s.day === day)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

  // مواعيد الحصص اللي ماشية عليها المرحلة المختارة في اليوم ده.
  const bellForDay = (day) =>
    bells.find(
      (b) =>
        (b.grades || []).some((g) => (g._id || g) === builderGrade) &&
        (b.days || []).includes(day),
    );
  const bellPeriods = (day) =>
    [...(bellForDay(day)?.periods || [])].sort((a, b) => a.period - b.period);

  // الحصص المحجوزة بالفعل للفصل ده في اليوم ده — عشان تظهر مقفولة في الاختيار.
  // وقت التعديل، الحصة اللي بتتعدل نفسها مش محسوبة محجوزة (ولا باقي لغات المادة الثانية).
  const takenPeriods = (day) => {
    const map = {};
    periodsForDay(day)
      .filter((s) => s._id !== editingPeriodId && !replaceIds.includes(s._id) && s.period)
      .forEach((s) => {
        map[s.period] = s;
      });
    return map;
  };

  const togglePeriod = (period) => {
    setPeriodForm((prev) => {
      // التعديل على حصة واحدة بس — اختيار حصة تانية بيبدّلها.
      if (editingPeriodId) return { ...prev, periods: [period] };
      const has = prev.periods.includes(period);
      return {
        ...prev,
        periods: has
          ? prev.periods.filter((p) => p !== period)
          : [...prev.periods, period].sort((a, b) => a - b),
      };
    });
  };

  const resetPeriodForm = () => {
    setPeriodForm({ periods: [], subjectId: "", teacherId: "", options: emptyOptions() });
    setPeriodError("");
    setEditingPeriodId(null);
    setReplaceIds([]);
  };

  const openBuilder = () => {
    setBuilderOpen(true);
    setBuilderGrade("");
    setBuilderClassroom("");
    setAddingDay(null);
    resetPeriodForm();
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setAddingDay(null);
    resetPeriodForm();
  };

  // "تعديل" على كارت حصة قائمة — يفتح المنشئ على نفس فصلها ومرحلتها، وياخد
  // بيانات الحصة دي في فورم الإضافة جاهزة للتعديل بدل الإضافة.
  const openBuilderForEdit = (schedule) => {
    setBuilderOpen(true);
    setBuilderGrade(schedule.classroom?.grade?._id || "");
    setBuilderClassroom(schedule.classroom?._id || "");
    setAddingDay(schedule.day);
    setEditingPeriodId(schedule._id);
    if (schedule.secondLanguage) {
      // A «مادة ثانية» period opens as a whole: every language, each with its teacher.
      const rows = siblingsOf(schedule);
      setReplaceIds(rows.map((s) => s._id));
      setPeriodForm({
        periods: schedule.period ? [schedule.period] : [],
        subjectId: SECOND_LANGUAGE,
        teacherId: "",
        options: rows.map((s) => ({ subjectId: s.subject?._id || "", teacherId: s.teacher?._id || "" })),
      });
    } else {
      setReplaceIds([schedule._id]);
      setPeriodForm({
        periods: schedule.period ? [schedule.period] : [],
        subjectId: schedule.subject?._id || "",
        teacherId: schedule.teacher?._id || NO_TEACHER,
        options: emptyOptions(),
      });
    }
    setPeriodError("");
  };

  // A blank square on the timetable: open the builder on that class and day
  // with that period already ticked.
  const openBuilderForSlot = (classroom, day, period) => {
    setBuilderOpen(true);
    setBuilderGrade(classroom.grade?._id || "");
    setBuilderClassroom(classroom._id);
    setAddingDay(day);
    resetPeriodForm();
    setPeriodForm({ periods: [period], subjectId: "", teacherId: "", options: emptyOptions() });
  };

  const startAddingPeriod = (day) => {
    setAddingDay(day);
    resetPeriodForm();
  };

  // The teacher a language of «مادة ثانية» starts with: the one assigned to
  // it in this class, else the only one teaching it at this grade.
  const defaultTeacherFor = (subjectId) => {
    const assigned = assignedTeacherFor(subjectId);
    if (assigned) return assigned._id;
    const able = teachers.filter(
      (t) =>
        (t.subjects || []).some((s) => s._id === subjectId) &&
        (t.teachingGrades || []).some((g) => g._id === builderGrade),
    );
    return able.length === 1 ? able[0]._id : "";
  };

  // Teachers who may take a language of «مادة ثانية» in this class.
  const teachersForOption = (subjectId) => {
    const assigned = assignedTeacherFor(subjectId);
    return teachers.filter((t) =>
      assigned
        ? t._id === assigned._id
        : (t.subjects || []).some((s) => s._id === subjectId) &&
          (t.teachingGrades || []).some((g) => g._id === builderGrade),
    );
  };

  const updateOption = (index, changes) =>
    setPeriodForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? { ...o, ...changes } : o)),
    }));

  const handleSubjectChange = (subjectId) => {
    if (subjectId === SECOND_LANGUAGE) {
      // Turning an ordinary lesson into «مادة ثانية» keeps it as the first language.
      setPeriodForm((prev) => {
        const keep =
          editingPeriodId && prev.subjectId && prev.subjectId !== SECOND_LANGUAGE && prev.teacherId !== NO_TEACHER
            ? [{ subjectId: prev.subjectId, teacherId: prev.teacherId }, { subjectId: "", teacherId: "" }]
            : emptyOptions();
        return { ...prev, subjectId, teacherId: "", options: keep };
      });
      return;
    }
    const assigned = assignedTeacherFor(subjectId);
    const teachersForSubject = teachers.filter(
      (t) =>
        (t.subjects || []).some((s) => s._id === subjectId) &&
        (t.teachingGrades || []).some((g) => g._id === builderGrade),
    );
    // A subject nobody teaches at this grade (نشاط, طابور) is almost always a
    // period that only needs its name on the timetable — start it there.
    setPeriodForm((prev) => ({
      ...prev,
      subjectId,
      teacherId: assigned
        ? assigned._id
        : teachersForSubject.length === 1
          ? teachersForSubject[0]._id
          : teachersForSubject.length === 0
            ? NO_TEACHER
            : "",
    }));
  };

  const handleSavePeriod = async (day) => {
    setPeriodError("");

    if (periodForm.periods.length === 0) {
      setPeriodError("اختار حصة واحدة على الأقل.");
      return;
    }

    if (periodForm.subjectId === SECOND_LANGUAGE) {
      const options = periodForm.options.filter((o) => o.subjectId || o.teacherId);
      if (options.length < 2 || options.some((o) => !o.subjectId || !o.teacherId)) {
        setPeriodError("المادة الثانية محتاجة لغتين على الأقل، وكل لغة بمعلمها.");
        return;
      }
      setPeriodSaving(true);
      try {
        const res = await API.post("/schedules", {
          secondLanguage: true,
          classroom: builderClassroom,
          day,
          periods: periodForm.periods,
          options: options.map((o) => ({ subjectId: o.subjectId, teacher: o.teacherId })),
          replaceIds: editingPeriodId ? replaceIds : [],
        });
        showToast(res.data?.message || "تم حفظ المادة الثانية", "success");
        setAddingDay(null);
        resetPeriodForm();
        fetchInitialData();
      } catch (err) {
        setPeriodError(err.response?.data?.message || "حدث خطأ أثناء الحفظ");
      } finally {
        setPeriodSaving(false);
      }
      return;
    }

    if (!periodForm.subjectId || !periodForm.teacherId) {
      setPeriodError('اختار المادة والمعلم (أو "بدون معلم").');
      return;
    }

    setPeriodSaving(true);
    try {
      const payload = {
        teacher: periodForm.teacherId === NO_TEACHER ? "" : periodForm.teacherId,
        subjectId: periodForm.subjectId,
        classroom: builderClassroom,
        day,
      };

      if (editingPeriodId) {
        await API.put(`/schedules/${editingPeriodId}`, {
          ...payload,
          period: periodForm.periods[0],
          ...(periodForm.teacherId === NO_TEACHER ? { noTeacher: true } : {}),
        });
        showToast("تم تحديث الحصة بنجاح", "success");
      } else {
        const res = await API.post("/schedules", {
          ...payload,
          periods: periodForm.periods,
        });
        showToast(res.data?.message || "تمت إضافة الحصة بنجاح", "success");
      }

      setAddingDay(null);
      resetPeriodForm();
      fetchInitialData();
    } catch (err) {
      setPeriodError(err.response?.data?.message || "حدث خطأ أثناء الحفظ");
    } finally {
      setPeriodSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8 font-sans" dir="rtl">
      {toast.show && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[60] animate-in slide-in-from-top-5 duration-300">
          <div
            className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-xl border ${
              toast.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle2 size={18} className="text-emerald-600" />
            ) : (
              <AlertCircle size={18} className="text-rose-600" />
            )}
            <span className="font-bold text-sm">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-800 flex items-center gap-3">
              <Calendar className="text-indigo-600" size={36} /> الجدول الدراسي
            </h1>
            <p className="text-slate-400 font-medium text-sm mt-1">
              إدارة وفلترة جدول الحصص الدراسية
            </p>
          </div>
          <button
            onClick={openBuilder}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 transition-colors text-white px-6 py-4 rounded-2xl font-black shadow-lg shadow-indigo-100"
          >
            <Plus size={20} /> بناء جدول فصل
          </button>
        </div>

        {/* شريط الفلترة (Filter Bar) */}
        <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-2 text-slate-500 font-bold text-sm bg-slate-50 px-4 py-3 rounded-2xl">
            <Filter size={16} className="text-indigo-500" />
            <span>فلترة حسب الفصل:</span>
          </div>
          <div className="w-full sm:w-72">
            <select
              className="w-full p-3.5 bg-slate-50 rounded-2xl border-2 border-slate-100 focus:border-indigo-500 outline-none font-bold text-slate-700 text-sm transition-all cursor-pointer"
              value={selectedClassroom}
              onChange={(e) => setSelectedClassroom(e.target.value)}
            >
              <option value="">كل الفصول والمراحل</option>
              {classrooms.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.grade?.name ? `${c.grade.name} - ` : ""}
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {selectedClassroom && (
            <button
              onClick={() => setSelectedClassroom("")}
              className="text-xs font-bold text-rose-500 hover:bg-rose-50 px-3 py-2 rounded-xl transition-colors"
            >
              مسح الفلتر
            </button>
          )}

          <button
            onClick={() => setTodayOnly((prev) => !prev)}
            className={`flex items-center gap-2 px-5 py-3.5 rounded-2xl font-black text-sm transition-all sm:mr-auto ${
              todayOnly
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-100"
                : "bg-slate-50 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600"
            }`}
          >
            <Calendar size={16} />
            {todayOnly ? `حصص اليوم (${daysMapping[todayKey] || "إجازة"})` : "حصص اليوم فقط"}
          </button>
        </div>

        {/* منطقة عرض البيانات (Cards Container) */}
        <div>
          {loading ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-6 flex justify-center py-40">
              <Loader2 className="animate-spin text-indigo-500" size={48} />
            </div>
          ) : selectedClassroom && !todayOnly ? (
            <ClassTimetable
              classroom={classrooms.find((c) => c._id === selectedClassroom)}
              lessons={schedules.filter((s) => s.classroom?._id === selectedClassroom)}
              bells={bells}
              canEdit={canEdit}
              onEdit={openBuilderForEdit}
              onAdd={(day, period) =>
                openBuilderForSlot(classrooms.find((c) => c._id === selectedClassroom), day, period)
              }
              onDelete={askDelete}
              onSecondLanguage={() => setLanguageClass(classrooms.find((c) => c._id === selectedClassroom))}
            />
          ) : filteredSchedules.length === 0 ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-100 p-6 text-center py-32 text-slate-400 font-bold uppercase tracking-wider">
              {todayOnly && !todayKey
                ? "النهاردة إجازة الأسبوع، مفيش حصص."
                : "لا يوجد جداول مطابقة لهذا الاختيار."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSchedules.map((s) => (
                <div
                  key={s._id}
                  className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all flex flex-col justify-between group relative overflow-hidden"
                >
                  {/* شريط علوي صغير لتحديد اليوم ملوّن */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-indigo-500/10 group-hover:bg-indigo-500 transition-colors" />

                  <div className="space-y-4">
                    {/* معلومات اليوم والفصل */}
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-black uppercase tracking-wider bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded-xl">
                        {daysMapping[s.day]}
                      </span>
                      <div className="flex items-center gap-1.5 text-slate-600 bg-slate-50 px-3 py-1.5 rounded-xl text-xs font-bold">
                        <Home size={14} className="text-slate-400" />
                        <span>
                          {s.classroom?.grade?.name} - {s.classroom?.name}
                        </span>
                      </div>
                    </div>

                    {/* المادة والـ Timing */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-amber-50 rounded-xl text-amber-600">
                          <BookOpen size={16} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-800 text-base leading-tight">
                            {s.subject?.name || "لا توجد مادة محددة"}
                          </h4>
                          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wide">
                            {s.secondLanguage ? "مادة ثانية" : "المادة"}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-1">
                        <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600">
                          <Clock size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-700">
                            {s.startTime} - {s.endTime}
                          </p>
                          <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wide">
                            {s.period ? `الحصة ${PERIOD_NAMES[s.period]}` : "توقيت الحصة"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* المدرس */}
                    <div className="pt-3 border-t border-slate-50 flex items-center gap-3">
                      <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center text-slate-600 font-bold text-sm">
                        {s.teacher?.firstName?.charAt(0) || <User size={16} />}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">
                          {s.teacher
                            ? `أ. ${s.teacher.firstName} ${s.teacher.lastName}`
                            : "بدون معلم"}
                        </p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                          المعلم
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* أزرار التحكم أسفل الكارد */}
                  <div className="flex gap-2 pt-5 mt-4 border-t border-slate-50/60">
                    <button
                      onClick={() => openBuilderForEdit(s)}
                      className="flex-1 py-2.5 bg-slate-50 hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 rounded-xl transition-all flex items-center justify-center gap-1.5 text-xs font-bold"
                    >
                      <Edit size={14} /> تعديل
                    </button>
                    <button
                      onClick={() => askDelete(s)}
                      className="py-2.5 px-3 bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-xl transition-all flex items-center justify-center text-xs font-bold"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ---- منشئ جدول الفصل ---- */}
      {builderOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-5xl rounded-[2.5rem] shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-8 border-b border-slate-100 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-black text-slate-800">
                  بناء جدول فصل
                </h2>
                <p className="text-slate-400 text-xs font-bold mt-1">
                  اختار المرحلة والفصل، وبعدين ضيف حصصه يوم بيوم في مكان واحد.
                </p>
              </div>
              <button
                onClick={closeBuilder}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X />
              </button>
            </div>

            <div className="p-8 overflow-y-auto flex-1 space-y-6">
              {/* اختيار المرحلة والفصل */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1">
                  <label className="text-xs font-black text-slate-400 mr-2 uppercase flex items-center gap-1">
                    <Layers size={13} /> المرحلة
                  </label>
                  <select
                    className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-slate-100 focus:border-indigo-500 bg-white outline-none font-bold text-slate-700 transition-all"
                    value={builderGrade}
                    onChange={(e) => {
                      setBuilderGrade(e.target.value);
                      setBuilderClassroom("");
                      setAddingDay(null);
                      resetPeriodForm();
                    }}
                  >
                    <option value="">اختر المرحلة...</option>
                    {grades.map((g) => (
                      <option key={g._id} value={g._id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-black text-slate-400 mr-2 uppercase flex items-center gap-1">
                    <Home size={13} /> الفصل
                  </label>
                  <select
                    disabled={!builderGrade}
                    className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-slate-100 focus:border-indigo-500 bg-white outline-none font-bold text-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    value={builderClassroom}
                    onChange={(e) => {
                      setBuilderClassroom(e.target.value);
                      setAddingDay(null);
                      resetPeriodForm();
                    }}
                  >
                    <option value="">
                      {builderGrade ? "اختر الفصل..." : "اختر المرحلة أولاً"}
                    </option>
                    {gradeClassrooms.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* جدول الأيام */}
              {builderClassroom && (
                <div className="space-y-4 pt-2">
                  <div className="flex items-center gap-2 text-indigo-600">
                    <Calendar size={18} />
                    <h3 className="font-black text-sm">
                      جدول فصل {currentClassroom?.name} — {currentClassroom?.grade?.name}
                    </h3>
                  </div>

                  <p className="text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-2.5">
                    عدد الحصص ومواعيدها في كل يوم جاية من صفحة "مواعيد الحصص".
                  </p>

                  {gradeSubjects.length === 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl text-amber-700 text-xs font-bold">
                      لا توجد مواد مُسندة لهذه المرحلة بعد — أضِف مواد من صفحة
                      "المواد الدراسية" أولًا.
                    </div>
                  )}

                  {dayOrder.map((day) => (
                    <div
                      key={day}
                      className="bg-slate-50/60 border border-slate-100 rounded-3xl p-5 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-black text-slate-700">
                          {daysMapping[day]}
                        </span>
                        {addingDay !== day && (
                          <button
                            type="button"
                            onClick={() => startAddingPeriod(day)}
                            disabled={gradeSubjects.length === 0}
                            className="flex items-center gap-1.5 text-xs font-black text-indigo-600 hover:bg-indigo-50 px-3 py-1.5 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus size={14} /> إضافة حصة
                          </button>
                        )}
                      </div>

                      {periodsForDay(day).length === 0 && addingDay !== day && (
                        <p className="text-[11px] font-bold text-slate-400">
                          مفيش حصص متسجّلة لهذا اليوم لسه.
                        </p>
                      )}

                      {periodsForDay(day)
                        // A «مادة ثانية» period is listed once, its languages together.
                        .filter((s) => !s.secondLanguage || siblingsOf(s)[0]?._id === s._id)
                        .map((s) => (
                        <div
                          key={s._id}
                          className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-slate-100 p-3"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {s.period && (
                              <span className="text-xs font-black text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg shrink-0">
                                الحصة {PERIOD_NAMES[s.period]}
                              </span>
                            )}
                            <span className="text-xs font-black text-slate-600 bg-slate-50 px-2.5 py-1 rounded-lg shrink-0">
                              {s.startTime}–{s.endTime}
                            </span>
                            {s.secondLanguage ? (
                              <>
                                <span className="text-sm font-black text-amber-700 shrink-0">مادة ثانية</span>
                                <span className="text-xs font-bold text-slate-500 truncate">
                                  {siblingsOf(s)
                                    .map((x) => `${x.subject?.name || ""} (أ. ${x.teacher?.firstName || ""} ${x.teacher?.lastName || ""})`)
                                    .join(" · ")}
                                </span>
                              </>
                            ) : (
                              <>
                                <span className="text-sm font-black text-slate-700 truncate">
                                  {s.subject?.name || "بدون مادة"}
                                </span>
                                <span className="text-xs font-bold text-slate-400 truncate">
                                  {s.teacher
                                    ? `أ. ${s.teacher.firstName} ${s.teacher.lastName}`
                                    : "بدون معلم"}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => openBuilderForEdit(s)}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => askDelete(s)}
                              className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}

                      {addingDay === day && (
                        <div className="bg-white rounded-2xl border-2 border-indigo-100 p-4 space-y-3">
                          {periodError && (
                            <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-2 text-rose-600 font-bold text-xs">
                              <AlertCircle size={14} /> {periodError}
                            </div>
                          )}
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-black text-slate-400 uppercase">
                              {editingPeriodId
                                ? "الحصة"
                                : "الحصص (تقدر تختار أكتر من حصة)"}
                            </label>
                            {bellPeriods(day).length === 0 && (
                              <p className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-amber-700 text-xs font-bold">
                                مفيش مواعيد حصص متحددة للمرحلة دي يوم {daysMapping[day]}. حددها من صفحة "مواعيد الحصص" الأول.
                              </p>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                              {bellPeriods(day).map(({ period: p, startTime, endTime }) => {
                                const taken = takenPeriods(day)[p];
                                const selected = periodForm.periods.includes(p);
                                const times = { startTime, endTime };
                                return (
                                  <button
                                    key={p}
                                    type="button"
                                    disabled={!!taken}
                                    onClick={() => togglePeriod(p)}
                                    title={
                                      taken
                                        ? `محجوزة: ${taken.subject?.name || ""} — ${taken.teacher ? `أ. ${taken.teacher.firstName}` : "بدون معلم"}`
                                        : ""
                                    }
                                    className={`p-2.5 rounded-xl border-2 text-center transition-all ${
                                      taken
                                        ? "bg-slate-100 border-slate-100 text-slate-300 cursor-not-allowed"
                                        : selected
                                          ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
                                          : "bg-white border-slate-100 text-slate-600 hover:border-indigo-300"
                                    }`}
                                  >
                                    <div className="text-xs font-black">
                                      {PERIOD_NAMES[p]}
                                    </div>
                                    <div
                                      className={`text-[10px] font-bold mt-0.5 ${
                                        selected ? "text-indigo-100" : "text-slate-400"
                                      }`}
                                    >
                                      {taken ? "محجوزة" : `${times.startTime}–${times.endTime}`}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-[10px] font-black text-slate-400 uppercase">
                                المادة
                              </label>
                              <select
                                className="w-full p-3 bg-slate-50 rounded-xl border-2 border-slate-100 focus:border-indigo-500 bg-white outline-none font-bold text-slate-700 text-sm transition-all"
                                value={periodForm.subjectId}
                                onChange={(e) => handleSubjectChange(e.target.value)}
                              >
                                <option value="">اختر المادة...</option>
                                <option value={SECOND_LANGUAGE}>مادة ثانية — لغتين في نفس الحصة (ألماني / فرنساوي)</option>
                                {gradeSubjects.map((sub) => {
                                  const assigned = assignedTeacherFor(sub._id);
                                  return (
                                    <option key={sub._id} value={sub._id}>
                                      {sub.name}
                                      {assigned ? ` — أ. ${assigned.firstName} ${assigned.lastName}` : ""}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {periodForm.subjectId !== SECOND_LANGUAGE && (
                            <div className="space-y-1">
                              <label className="text-[10px] font-black text-slate-400 uppercase">
                                المعلم
                              </label>
                              <select
                                disabled={!periodForm.subjectId}
                                className="w-full p-3 bg-slate-50 rounded-xl border-2 border-slate-100 focus:border-indigo-500 bg-white outline-none font-bold text-slate-700 text-sm transition-all disabled:opacity-50"
                                value={periodForm.teacherId}
                                onChange={(e) =>
                                  setPeriodForm((prev) => ({
                                    ...prev,
                                    teacherId: e.target.value,
                                  }))
                                }
                              >
                                <option value="">
                                  {periodForm.subjectId
                                    ? "اختر المعلم..."
                                    : "اختر المادة أولاً"}
                                </option>
                                {eligibleTeachers.map((t) => (
                                  <option key={t._id} value={t._id}>
                                    {t.firstName} {t.lastName}
                                  </option>
                                ))}
                                <option value={NO_TEACHER}>بدون معلم — اسم المادة بس</option>
                              </select>
                              {assignedTeacher && periodForm.teacherId !== NO_TEACHER && (
                                <p className="text-[10px] font-bold text-indigo-500">
                                  المادة دي متسندة للمعلم ده في الفصل من صفحة "إسناد الفصول".
                                </p>
                              )}
                              {periodForm.teacherId === NO_TEACHER ? (
                                <p className="text-[10px] font-bold text-slate-500">
                                  الحصة هتظهر في جدول الفصل باسم المادة بس — من غير غياب ولا درجات،
                                  ومش هتظهر لأي معلم.
                                </p>
                              ) : (
                                periodForm.subjectId &&
                                eligibleTeachers.length === 0 && (
                                  <p className="text-[10px] font-bold text-rose-500">
                                    مفيش معلم مسند لهذه المادة في هذه المرحلة.
                                  </p>
                                )
                              )}
                            </div>
                            )}
                          </div>

                          {periodForm.subjectId === SECOND_LANGUAGE && (
                            <div className="space-y-2 bg-amber-50/60 border border-amber-100 rounded-2xl p-3">
                              <p className="text-[11px] font-bold text-amber-800">
                                الفصل بيتقسم في الحصة دي: كل طالب يحضر لغته مع مدرسها. وزّع الطلاب على اللغات بعد الحفظ من زرار «توزيع الطلاب على المادة الثانية» فوق الجدول.
                              </p>
                              {periodForm.options.map((option, index) => {
                                const able = option.subjectId ? teachersForOption(option.subjectId) : [];
                                return (
                                  <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                                    <select
                                      className="w-full p-3 rounded-xl border-2 border-slate-100 focus:border-indigo-500 bg-white outline-none font-bold text-slate-700 text-sm"
                                      value={option.subjectId}
                                      onChange={(e) =>
                                        updateOption(index, {
                                          subjectId: e.target.value,
                                          teacherId: e.target.value ? defaultTeacherFor(e.target.value) : "",
                                        })
                                      }
                                    >
                                      <option value="">اللغة {index + 1}...</option>
                                      {gradeSubjects.map((sub) => (
                                        <option key={sub._id} value={sub._id}>
                                          {sub.name}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      disabled={!option.subjectId}
                                      className="w-full p-3 rounded-xl border-2 border-slate-100 focus:border-indigo-500 bg-white outline-none font-bold text-slate-700 text-sm disabled:opacity-50"
                                      value={option.teacherId}
                                      onChange={(e) => updateOption(index, { teacherId: e.target.value })}
                                    >
                                      <option value="">{option.subjectId ? "اختر المعلم..." : "اختر اللغة أولاً"}</option>
                                      {able.map((t) => (
                                        <option key={t._id} value={t._id}>
                                          {t.firstName} {t.lastName}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      disabled={periodForm.options.length <= 2}
                                      onClick={() =>
                                        setPeriodForm((prev) => ({
                                          ...prev,
                                          options: prev.options.filter((_, i) => i !== index),
                                        }))
                                      }
                                      className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                                      aria-label="شيل اللغة دي"
                                    >
                                      <X size={14} />
                                    </button>
                                    {option.subjectId && able.length === 0 && (
                                      <p className="sm:col-span-3 text-[10px] font-bold text-rose-500">
                                        مفيش معلم بيدرّس المادة دي للمرحلة دي.
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() =>
                                  setPeriodForm((prev) => ({
                                    ...prev,
                                    options: [...prev.options, { subjectId: "", teacherId: "" }],
                                  }))
                                }
                                className="flex items-center gap-1.5 text-xs font-black text-amber-800 hover:bg-amber-100 px-3 py-1.5 rounded-xl transition-all"
                              >
                                <Plus size={13} /> لغة تالتة
                              </button>
                            </div>
                          )}

                          <div className="flex gap-2 pt-1">
                            <button
                              type="button"
                              disabled={periodSaving}
                              onClick={() => handleSavePeriod(day)}
                              className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 transition-colors text-white rounded-xl font-black flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                            >
                              {periodSaving ? (
                                <Loader2 className="animate-spin" size={16} />
                              ) : (
                                <Save size={16} />
                              )}
                              {editingPeriodId
                                ? "تحديث الحصة"
                                : periodForm.periods.length > 1
                                  ? `حفظ ${periodForm.periods.length} حصص`
                                  : "حفظ الحصة"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setAddingDay(null);
                                resetPeriodForm();
                              }}
                              className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-xl font-bold text-sm transition-all"
                            >
                              إلغاء
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {languageClass && (
        <SecondLanguageAssign
          classroom={languageClass}
          canEdit={canEdit}
          onClose={() => setLanguageClass(null)}
          onSaved={(message) => showToast(message, "success")}
        />
      )}

      {deleteIds && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-6 text-center space-y-6">
            <div className="w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto text-rose-500">
              <Trash2 size={30} />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-black text-slate-800">
                حذف الحصة
              </h3>
              <p className="text-sm font-medium text-slate-500">
                هل أنت متأكد من حذف هذه الحصة؟ لا يمكن التراجع عن هذا الإجراء.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteIds(null)}
                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-700 text-white font-black rounded-xl shadow-lg shadow-rose-100 transition-all"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulesPage;
